import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { seedCatalogueIfNeeded, type SeedProgress } from '../data/seed/seedCatalogue';

/**
 * First-run catalogue seeding. Phase 0 §7.3.
 *
 * Phase 3 built the seeding mechanism and wired it to a button in Settings.
 * That left a real gap: a fresh install had no exercises until the user went
 * looking for a control they had no reason to know about. This closes it.
 *
 * Three properties, each deliberate:
 *
 *   1. **It does not block the application.** Seeding runs after first paint,
 *      not before it. Every other screen — Settings, appearance, the shell —
 *      is usable while 1,500 rows are written. Only the Exercises screen has
 *      anything to wait for, and it is the only screen that shows the wait.
 *   2. **It calls `seedCatalogueIfNeeded` and nothing else.** There is exactly
 *      one seeding implementation (Phase 3's), and this is a trigger for it,
 *      not a second copy. Idempotence, resumability and the guarantee that
 *      user-owned state survives all come from there and are tested there.
 *   3. **It reads bundled assets.** `/catalogue/exercises.json` ships with the
 *      app and is precached; there is no network fetch to the internet, and
 *      seeding works on a device that has never been online.
 *
 * The Settings card keeps its own control — it is now a repair/verify action
 * rather than the only way to get a catalogue.
 */

export type CatalogueStatus =
  /** Haven't looked yet — the very first moments after mount. */
  | 'checking'
  /** Actively writing rows. `progress` is meaningful. */
  | 'seeding'
  /** Rows are present and complete. */
  | 'ready'
  /** Seeding failed. The message says why; the app is still usable. */
  | 'failed';

export interface CatalogueContextValue {
  status: CatalogueStatus;
  progress: SeedProgress | null;
  error: string | null;
  /** Retry after a failure, or re-check on demand. */
  refresh: () => Promise<void>;
}

const CatalogueContext = createContext<CatalogueContextValue | undefined>(undefined);

export function CatalogueProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<CatalogueStatus>('checking');
  const [progress, setProgress] = useState<SeedProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      // `seedCatalogueIfNeeded` returns null when there is nothing to do, which
      // is the common case on every launch after the first — so a normal start
      // never enters the 'seeding' state and never flashes a progress message.
      let started = false;
      const result = await seedCatalogueIfNeeded({
        onProgress: (value) => {
          if (!started) {
            started = true;
            setStatus('seeding');
          }
          setProgress(value);
        },
      });

      void result;
      setStatus('ready');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The exercise catalogue could not be prepared.');
      setStatus('failed');
    } finally {
      setProgress(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      await run();
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [run]);

  const value = useMemo<CatalogueContextValue>(
    () => ({ status, progress, error, refresh: run }),
    [status, progress, error, run],
  );

  return <CatalogueContext.Provider value={value}>{children}</CatalogueContext.Provider>;
}

export function useCatalogue(): CatalogueContextValue {
  const context = useContext(CatalogueContext);
  if (context === undefined) {
    throw new Error('useCatalogue must be used inside a CatalogueProvider');
  }
  return context;
}
